import PropertiesService, { ListItemModel as PropertyListItemModel } from '@/modules/renter/properties/services/PropertiesService';
import UsersService, { DepartmentEnum, ListItemModel as UserItemModel } from '@/modules/core/users/services/UsersService';
import RegionsService, { ListItemModel as RegionItemModel } from '@/modules/renter/regions/services/RegionsService';
import CitiesService, { ListItemModel as CityItemModel } from '@/modules/renter/cities/services/CitiesService';
import ChecklistsService, { ListItemModel as ChecklistTemplateModel } from '@/modules/renter/checklists/services/ChecklistsService';
import CompaniesService, { ListItemModel as CompanyListItemModel } from '@/modules/renter/companies/services/CompaniesService';
import SuppliesService from '@/modules/renter/supplies/services/SuppliesService';
import { Statement } from '@/helpers/Interfaces';
import OwnersService, { ListItemModel as OwnersListItemModel } from '@/modules/renter/owners/services/OwnersService';
import TagsService, { ListItemModel as TagsListItemModel } from '@/modules/renter/tags/services/TagsService';
import TasksTagsService from '@/modules/renter/tasksTags/services/TasksTagsService';
import DistrictsService, { ListItemModel as DistrictsListItemModel } from '@/modules/renter/districts/services/DistrictsService';
import ReservationsService, { ListItemModel as ReservationListItemModel } from '../../reservations/services/ReservationsService';
import { DepartmentEnum as DepartmentEnumChecklist } from '@/modules/renter/tasks/services/TasksService';

export const fetchProperty = async (publicId: string): Promise<PropertyListItemModel> =>
{
    if (!publicId.length) return null;

    const response = await PropertiesService.fetchAutocompleteModel(publicId);

    return response || null;
};

export const fetchPropertyV2 = async (publicId: string): Promise<PropertyListItemModel> =>
{
    const response = await PropertiesService.fetchAutocompleteModelV2(publicId);

    return response || null;
};

export const searchProperty = async (query: string, limit: number, allRecordsVisible: boolean = true): Promise<PropertyListItemModel[]> =>
{
    const response = await PropertiesService.getAutocompleteList(query, limit, allRecordsVisible);

    return response;
};

export const searchPropertyV2 = async (query: string, limit: number): Promise<PropertyListItemModel[]> =>
{
    const response = await PropertiesService.getAutocompleteListV2(query, limit);

    return response;
};

export const fetchCustomProperty = async (publicId: string): Promise<PropertyListItemModel> =>
{
    const response = await PropertiesService.fetchAutocompleteCustomModel(publicId);

    return response || null;
};

export const searchCustomProperty = async (query: string, limit: number): Promise<PropertyListItemModel[]> =>
{
    const response = await PropertiesService.getAutocompleteCustomList(query, limit);

    return response;
};


export const fetchUser = async (publicId: string): Promise<UserItemModel> =>
{
    const response = await UsersService.fetchAutocompletePublicIdModel(publicId);

    return response || null;
};

export const searchUser = async (query: string, limit: number, department: string = '', showAllRecords: boolean = true, propertyPublicId: string | null = null, customPropertyPublicId: string | null = null, userOutsideCurrentDistricts: boolean = false, cityPublicId: string | null = null, districtPublicId: string | null = null): Promise<UserItemModel[]> =>
{
    const response = await UsersService.getAutocompletePublicIdList(query, limit, department || '', showAllRecords, propertyPublicId, customPropertyPublicId, userOutsideCurrentDistricts, cityPublicId, districtPublicId);

    const list = response.map(item =>
    {
        const { cities } = item;

        item.citiesNames = cities.map(city => city.city).join(', ');

        return item;
    });

    return list;
};

export const searchDriver = async (query: string, limit: number, department: string = '', showAllRecords: boolean = true, propertyPublicId: string | null = null, customPropertyPublicId: string | null = null, userOutsideCurrentDistricts: boolean = false): Promise<UserItemModel[]> =>
{
    const response = await UsersService.getAutocompleteDriversPublicIdList(query, limit, department || '', showAllRecords, propertyPublicId, customPropertyPublicId, userOutsideCurrentDistricts);

    const list = response.map(item =>
    {
        const { cities } = item;

        item.citiesNames = cities.map(city => city.city).join(', ');

        return item;
    });

    return list;
};

export const fetchUserById = async (id: number): Promise<UserItemModel> =>
{
    const response = await UsersService.fetchAutocompleteModel(id);

    return response || null;
};

export const searchUserById = async (query: string, limit: number): Promise<UserItemModel[]> =>
{
    return await UsersService.getAutocompleteList(query, limit);
};

export const fetchRegion = async (publicId: string): Promise<RegionItemModel> =>
{
    const response = await RegionsService.fetchRegionAutocompleteModel(publicId);

    return response || null;
};

export const searchRegion = async (query: string, limit: number): Promise<RegionItemModel[]> =>
{
    return await RegionsService.getRegionsAutocompleteList(query, limit);
};

export const fetchCity = async (publicId: string): Promise<CityItemModel> =>
{
    const response = await CitiesService.fetchCityAutocompleteModel(publicId);

    return response || null;
};

export const searchCity = async (query: string, limit: number, showAllRecords: boolean = true): Promise<CityItemModel[]> =>
{
    return await CitiesService.getCitiesAutocompleteList(query, limit, showAllRecords);
};

export const fetchChecklist = async (publicId: string): Promise<ChecklistTemplateModel> =>
{
    const response = await ChecklistsService.fetchAutocompleteModel(publicId);

    return response || null;
};

export const searchChecklist = async (query: string, limit: number, department: DepartmentEnumChecklist, cityId: number,): Promise<ChecklistTemplateModel[]> =>
{
    return await ChecklistsService.getAutocompleteList(query, limit, department, cityId);
};

export const fetchCompany = async (publicId: string): Promise<CompanyListItemModel> =>
{
    const response = await CompaniesService.fetchAutocompleteModel(publicId);

    return response || null;
};

export const searchCompany = async (query: string, limit: number): Promise<CompanyListItemModel[]> =>
{
    const response = await CompaniesService.getAutocompleteList(query, limit);

    return response;
};

export const fetchSupply = async (publicId: string): Promise<Statement> =>
{
    const response = await SuppliesService.fetchAutocompleteSupply(publicId);

    return response || null;
};

export const searchSupply = async (query: string, limit: number): Promise<Statement> =>
{
    const response = await SuppliesService.fetchAutocompleteSupplyList(query, limit);

    return response;
};

export const searchOwner = async (query: string, limit: number): Promise<OwnersListItemModel[]> =>
{
    const response = await OwnersService.getAutocompleteList(query, limit);

    return response;
};

export const fetchOwner = async (publicId: string): Promise<OwnersListItemModel> =>
{
    const response = await OwnersService.fetchAutocompleteModel(publicId);

    return response || null;
};

export const searchTag = async (query: string, limit: number): Promise<TagsListItemModel[]> =>
{
    const response = await TagsService.getAutocompleteList(query, limit);

    return response;
};

export const fetchTag = async (publicId: string): Promise<TagsListItemModel> =>
{
    const response = await TagsService.fetchAutocompleteModel(publicId);

    return response || null;
};

export const searchDistrict = async (query: string, limit: number, citiesIds: number[] = [], showAllRecords: boolean = false): Promise<DistrictsListItemModel[]> =>
{
    const response = await DistrictsService.getDistrictsAutocompleteList(query, limit, citiesIds, showAllRecords);

    return response;
};

export const fetchDistrict = async (publicId: string): Promise<DistrictsListItemModel> =>
{
    const response = await DistrictsService.fetchDistrictAutocompleteModel(publicId);

    return response || null;
};

export const searchReservation = async (query: string, limit: number, propertyPublicId: string = null): Promise<ReservationListItemModel[]> =>
{
    const response = await ReservationsService.getReservationsAutocompleteList(query, limit, propertyPublicId);

    return response;
};

export const fetchReservation = async (publicId: string): Promise<ReservationListItemModel> =>
{
    const response = await ReservationsService.fetchReservationAutocompleteModel(publicId);

    return response || null;
};

export const searchReservationWebSource = async (query: string, limit: number): Promise<string[]> =>
{
    const response = await ReservationsService.getReservationsWebSourceAutocompleteList(query, limit);

    return response;
};

export const fetchReservationWebSource = async (publicId: string): Promise<string> =>
{
    const response = await ReservationsService.fetchReservationWebSourceAutocompleteModel(publicId);

    return response || null;
};

export const searchTasksTag = async (query: string, limit: number): Promise<TagsListItemModel[]> =>
{
    const response = await TasksTagsService.getAutocompleteList(query, limit);

    return response;
};

export const fetchDriver = async (publicId: string): Promise<UserItemModel> =>
{
    const response = await UsersService.fetchAutocompleteDriverPublicIdModel(publicId);

    return response || null;
};

export interface OptionText{
    join: string;
    options: Array<string>;
}
export interface AutocompleteOption{
    value: string;
    label: string;
    placeholder: string;
    search: any;
    fetch: any;
    optionText: OptionText;
    optionSubtext: OptionText;
}
